import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { showings } from '@prisma/client';
import { isBefore } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DateTimeSelector from '../DateTimeSelector/DateTimeSelector';
import showingsService from '../../api/services/showings';
import { useAppDispatch } from '../../store/store';
import { fetchShowings } from '../../store/slices/showings';
import { useAuth } from '../../contexts/AuthContext';
import formatTime from '../../utils/formatTime';
import useIsMobile from '../../hooks/useIsMobile';
import Circle from '../Circle/Circle';
import { useTheme } from '@mui/material/styles';
import ShowingAgent, {
  AGENT_AWAITING_CONFIRMATION_LABEL,
  isAgentAwaitingConfirmation,
} from '../ShowingAgent/ShowingAgent';

interface ShowingTimeIntegratedProps {
  showing: showings;
  admin?: boolean;
  onUpdate?: () => void;
}

const ShowingTimeIntegrated: React.FC<ShowingTimeIntegratedProps> = ({
  showing,
  admin = false,
  onUpdate,
}) => {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [showingAgentCanBook, setShowingAgentCanBook] = useState(true);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [altTimes, setAltTimes] = useState<Date[]>([new Date()]);
  const [altTimesError, setAltTimesError] = useState<string | null>(null);

  const showingTime = new Date(showing.proposedTime);
  const showingText = formatTime(showingTime);
  // Declined showings are soft-deleted (accepted stays false, cancelledAt set),
  // so pending requires no cancelledAt or the Deny button would reappear.
  const cancelled = showing.accepted === null || !!showing.cancelledAt;
  const showingPending = showing.accepted === false && !cancelled;
  const completed = showing.accepted && isBefore(showingTime, new Date());
  const confirmed = showing.accepted === true && !completed;
  const agentAwaitingConfirmation = isAgentAwaitingConfirmation(showing);
  // The assigned agent stays visible once confirmed and after the showing, and
  // is shown while awaiting confirmation so the buyer knows who to expect.
  const showAgentRow =
    !cancelled &&
    !!showing.showingAgentName &&
    (showing.accepted === true || agentAwaitingConfirmation);

  // Determine status circle color
  const getStatusColor = () => {
    if (confirmed) return theme.palette.success.main; // Green for confirmed
    if (showingPending) return '#f1c40f'; // Yellow for pending
    if (cancelled) return theme.palette.error.main; // Red for cancelled
    return theme.palette.grey[400]; // Grey for completed
  };

  const isBuyer = currentUser?.id === showing.userId;
  const isSeller = currentUser?.id !== showing.userId;

  const updateShowing = async (confirm: boolean, altTimesToSend?: Date[]) => {
    setLoading(true);

    const patchData: any = {
      accepted: confirm,
      agentBooks: showingAgentCanBook,
    };
    if (!confirm && altTimesToSend && altTimesToSend.length > 0) {
      patchData.suggestedTimes = altTimesToSend;
    }

    try {
      await showingsService.patch(showing?.id, patchData);
      dispatch(fetchShowings());
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update showing:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelShowing = async () => {
    setLoading(true);

    try {
      await showingsService.patch(showing?.id, {
        accepted: null,
      });
      dispatch(fetchShowings());
      onUpdate?.();
    } catch (error) {
      console.error('Failed to cancel showing:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      spacing={1}
      sx={{ width: '100%' }}
      alignItems={isMobile ? 'flex-start' : 'center'}
    >
      {/* Time Display */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isMobile ? 'space-between' : 'center'}
        flexWrap="wrap"
        gap={1}
        sx={{ width: '100%' }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Circle size={12} color={getStatusColor()} />
          <Typography variant="caption" color="text.secondary">
            {showingText}
          </Typography>
        </Stack>
        {loading && <CircularProgress size={16} />}
      </Stack>

      {agentAwaitingConfirmation && (
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {AGENT_AWAITING_CONFIRMATION_LABEL}
        </Typography>
      )}
      {showAgentRow && <ShowingAgent showing={showing} dense />}

      {/* Action Buttons */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        justifyContent={isMobile ? 'flex-start' : 'center'}
      >
        {/* Seller actions for pending tours */}
        {showingPending && !loading && isSeller && (
          <>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => updateShowing(true)}
              sx={{ fontSize: '0.75rem', py: 0.5, minHeight: 28 }}
            >
              Confirm
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => admin ? updateShowing(false) : setDenyModalOpen(true)}
              sx={{ fontSize: '0.75rem', py: 0.5, minHeight: 28 }}
            >
              Deny
            </Button>
            {admin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showingAgentCanBook}
                    onChange={(e) => setShowingAgentCanBook(e.target.checked)}
                    color="primary"
                    disabled={loading}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Agent Books</Typography>}
                sx={{ ml: 1 }}
              />
            )}
          </>
        )}

        {/* Buyer action to cancel tour (pending or confirmed) */}
        {(showingPending || confirmed) && !loading && isBuyer && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={cancelShowing}
            sx={{ fontSize: '0.75rem', py: 0.5, minHeight: 28 }}
          >
            Cancel
          </Button>
        )}
      </Stack>

      {/* Deny Modal with Alternative Times */}
      <Dialog
        open={denyModalOpen}
        onClose={() => setDenyModalOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Suggest Other Times</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              You must suggest at least one other available time for this
              showing request.
            </Typography>
            {altTimes.map((time, idx) => (
              <Stack direction="row" spacing={1} alignItems="center" key={idx}>
                <DateTimeSelector
                  value={time}
                  onChange={(newValue) => {
                    const updated = [...altTimes];
                    updated[idx] = newValue as Date;
                    setAltTimes(updated);
                  }}
                  minDate={new Date()}
                  showLabel={false}
                  fullWidth
                />
                {altTimes.length > 1 && (
                  <IconButton
                    onClick={() => {
                      setAltTimes(altTimes.filter((_, i) => i !== idx));
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAltTimes([...altTimes, new Date()])}
              sx={{ mt: 1 }}
            >
              Add Another Time
            </Button>
            {altTimesError && (
              <Typography color="error" variant="body2">
                {altTimesError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              // Filter out invalid/empty times
              const validTimes = altTimes.filter(Boolean);
              if (!validTimes.length) {
                setAltTimesError('You must suggest at least one valid time.');
                return;
              }
              setAltTimesError(null);
              setDenyModalOpen(false);
              await updateShowing(false, validTimes);
            }}
          >
            Deny & Send Times
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ShowingTimeIntegrated;
